import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { printFormatApi } from "@/lib/api";
import { toast } from "sonner";
import { 
  FileImage, 
  Save, 
  Trash2, 
  Upload, 
  Building2, 
  Palette,
  User,
  FileText,
  Eye,
  Loader2
} from "lucide-react";

export default function PrintFormat() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    lab_name: "",
    lab_slogan: "",
    lab_address: "",
    lab_phone: "",
    lab_email: "",
    logo_header: null,
    logo_footer: null,
    logo_background: null,
    signature_image: null,
    header_color: "#1a365d",
    show_qr: false,
    footer_text: "",
    responsible_name: "",
    responsible_title: "",
    responsible_license: ""
  });
  
  const headerLogoRef = useRef(null);
  const footerLogoRef = useRef(null);
  const backgroundLogoRef = useRef(null);
  const signatureRef = useRef(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await printFormatApi.get();
      setConfig(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error("Error fetching config:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await printFormatApi.save(config);
      toast.success("Configuración guardada exitosamente");
    } catch (error) {
      toast.error("Error al guardar la configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (type, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Por favor seleccione una imagen válida");
      return;
    }

    // Validate file size (max 20MB)
    if (file.size > 20 * 1024 * 1024) {
      toast.error("La imagen no debe superar 20MB");
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result;
      const field = type === "header" ? "logo_header" : "logo_footer";
      const newConfig = { ...config, [field]: base64 };
      setConfig(newConfig);
      
      // Auto-save after uploading logo
      try {
        await printFormatApi.save(newConfig);
        toast.success(`Logo ${type === "header" ? "de encabezado" : "de pie"} guardado exitosamente`);
      } catch (error) {
        toast.error("Error al guardar el logo");
        console.error("Error saving logo:", error);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteLogo = async (type) => {
    const field = type === "header" ? "logo_header" : "logo_footer";
    const newConfig = { ...config, [field]: null };
    setConfig(newConfig);
    
    // Auto-save after deleting logo
    try {
      await printFormatApi.save(newConfig);
      toast.success(`Logo ${type === "header" ? "de encabezado" : "de pie"} eliminado`);
    } catch (error) {
      toast.error("Error al eliminar el logo");
    }
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Por favor seleccione una imagen válida");
      return;
    }

    if (file.size > 1 * 1024 * 1024) {
      toast.error("La firma no debe superar 1MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result;
      const newConfig = { ...config, signature_image: base64 };
      setConfig(newConfig);
      
      // Auto-save after uploading signature
      try {
        await printFormatApi.save(newConfig);
        toast.success("Firma digital guardada exitosamente");
      } catch (error) {
        toast.error("Error al guardar la firma digital");
        console.error("Error saving signature:", error);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteSignature = async () => {
    const newConfig = { ...config, signature_image: null };
    setConfig(newConfig);
    
    // Auto-save after deleting signature
    try {
      await printFormatApi.save(newConfig);
      toast.success("Firma digital eliminada");
    } catch (error) {
      toast.error("Error al eliminar la firma digital");
    }
  };

  const handleBackgroundLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Por favor seleccione una imagen válida");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("La imagen no debe superar 2MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result;
      const newConfig = { ...config, logo_background: base64 };
      setConfig(newConfig);
      
      // Auto-save after uploading background logo
      try {
        await printFormatApi.save(newConfig);
        toast.success("Logo de fondo guardado exitosamente");
      } catch (error) {
        toast.error("Error al guardar el logo de fondo");
        console.error("Error saving background logo:", error);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteBackgroundLogo = async () => {
    const newConfig = { ...config, logo_background: null };
    setConfig(newConfig);
    
    // Auto-save after deleting background logo
    try {
      await printFormatApi.save(newConfig);
      toast.success("Logo de fondo eliminado");
    } catch (error) {
      toast.error("Error al eliminar el logo de fondo");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="h-6 w-6" />
            Formato de Impresión de Resultados
          </h1>
          <p className="text-muted-foreground">
            Configure el diseño de los reportes de resultados con los datos de su laboratorio
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Guardar Configuración
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Información del Laboratorio */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Información del Laboratorio
            </CardTitle>
            <CardDescription>
              Datos que aparecerán en el encabezado de los reportes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="lab_name">Nombre del Laboratorio</Label>
              <Input
                id="lab_name"
                value={config.lab_name}
                onChange={(e) => handleChange("lab_name", e.target.value)}
                placeholder="LABORATORIO CLÍNICO (opcional si usa logo)"
              />
              <p className="text-xs text-muted-foreground">
                Si su logo ya incluye el nombre, puede dejar este campo vacío
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="lab_slogan">Eslogan o Descripción</Label>
              <Input
                id="lab_slogan"
                value={config.lab_slogan || ""}
                onChange={(e) => handleChange("lab_slogan", e.target.value)}
                placeholder="Calidad y confianza en cada análisis"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lab_address">Dirección</Label>
              <Input
                id="lab_address"
                value={config.lab_address || ""}
                onChange={(e) => handleChange("lab_address", e.target.value)}
                placeholder="Av. Principal #123, Col. Centro"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="lab_phone">Teléfono</Label>
                <Input
                  id="lab_phone"
                  value={config.lab_phone || ""}
                  onChange={(e) => handleChange("lab_phone", e.target.value)}
                  placeholder="(123) 456-7890"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lab_email">Email</Label>
                <Input
                  id="lab_email"
                  type="email"
                  value={config.lab_email || ""}
                  onChange={(e) => handleChange("lab_email", e.target.value)}
                  placeholder="contacto@laboratorio.com"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="header_color">Color del Encabezado</Label>
              <div className="flex gap-2">
                <Input
                  id="header_color"
                  type="color"
                  value={config.header_color}
                  onChange={(e) => handleChange("header_color", e.target.value)}
                  className="w-16 h-10 p-1 cursor-pointer"
                />
                <Input
                  value={config.header_color}
                  onChange={(e) => handleChange("header_color", e.target.value)}
                  placeholder="#1a365d"
                  className="flex-1"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logotipos */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileImage className="h-5 w-5" />
              Logotipos
            </CardTitle>
            <CardDescription>
              Suba los logotipos de su laboratorio (PNG, JPG - máx 20MB)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Logo de Encabezado */}
            <div className="space-y-3">
              <Label>Logo de Encabezado</Label>
              <div className="flex items-start gap-4">
                <div className="w-32 h-24 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted/30 overflow-hidden">
                  {config.logo_header ? (
                    <img 
                      src={config.logo_header} 
                      alt="Logo encabezado" 
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <FileImage className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    type="file"
                    ref={headerLogoRef}
                    accept="image/*"
                    onChange={(e) => handleLogoUpload("header", e)}
                    className="hidden"
                  />
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => headerLogoRef.current?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Subir Logo
                  </Button>
                  {config.logo_header && (
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => handleDeleteLogo("header")}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Eliminar
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <Separator />

            {/* Logo de Pie */}
            <div className="space-y-3">
              <Label>Logo de Pie de Página (opcional)</Label>
              <div className="flex items-start gap-4">
                <div className="w-32 h-24 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted/30 overflow-hidden">
                  {config.logo_footer ? (
                    <img 
                      src={config.logo_footer} 
                      alt="Logo pie" 
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <FileImage className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    type="file"
                    ref={footerLogoRef}
                    accept="image/*"
                    onChange={(e) => handleLogoUpload("footer", e)}
                    className="hidden"
                  />
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => footerLogoRef.current?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Subir Logo
                  </Button>
                  {config.logo_footer && (
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => handleDeleteLogo("footer")}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Eliminar
                    </Button>
                  )}
                </div>
              </div>

              {/* Firma Digital */}
              <div className="border rounded-lg p-4">
                <Label className="text-sm font-medium mb-2 block">Firma Digital</Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Esta firma aparecerá en el área de firma de todos los resultados
                </p>
                <div className="border-2 border-dashed rounded-lg p-4 text-center min-h-[100px] flex items-center justify-center bg-muted/30">
                  {config.signature_image ? (
                    <img 
                      src={config.signature_image} 
                      alt="Firma digital" 
                      className="max-h-[80px] object-contain"
                    />
                  ) : (
                    <div className="text-muted-foreground text-sm">
                      <Upload className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      Sin firma digital
                    </div>
                  )}
                </div>
                <div className="flex gap-2 mt-3">
                  <input
                    type="file"
                    ref={signatureRef}
                    accept="image/*"
                    onChange={handleSignatureUpload}
                    className="hidden"
                  />
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => signatureRef.current?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Subir Firma
                  </Button>
                  {config.signature_image && (
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={handleDeleteSignature}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Eliminar
                    </Button>
                  )}
                </div>
              </div>

              {/* Logo de Fondo (Marca de agua) */}
              <div className="border rounded-lg p-4">
                <Label className="text-sm font-medium mb-2 block">Logo de Fondo (Marca de agua)</Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Este logo aparecerá como marca de agua en el centro de cada página
                </p>
                <div className="border-2 border-dashed rounded-lg p-4 text-center min-h-[100px] flex items-center justify-center bg-muted/30">
                  {config.logo_background ? (
                    <img 
                      src={config.logo_background} 
                      alt="Logo de fondo" 
                      className="max-h-[80px] object-contain opacity-50"
                    />
                  ) : (
                    <div className="text-muted-foreground text-sm">
                      <Upload className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      Sin logo de fondo
                    </div>
                  )}
                </div>
                <div className="flex gap-2 mt-3">
                  <input
                    type="file"
                    ref={backgroundLogoRef}
                    accept="image/*"
                    onChange={handleBackgroundLogoUpload}
                    className="hidden"
                  />
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => backgroundLogoRef.current?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Subir Logo
                  </Button>
                  {config.logo_background && (
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={handleDeleteBackgroundLogo}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Eliminar
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Responsable */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Responsable / Firma
            </CardTitle>
            <CardDescription>
              Información del responsable que firmará los reportes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="responsible_name">Nombre del Responsable</Label>
              <Input
                id="responsible_name"
                value={config.responsible_name || ""}
                onChange={(e) => handleChange("responsible_name", e.target.value)}
                placeholder="Q.F.B. Juan Pérez López"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="responsible_title">Título / Cargo</Label>
              <Input
                id="responsible_title"
                value={config.responsible_title || ""}
                onChange={(e) => handleChange("responsible_title", e.target.value)}
                placeholder="Químico Farmacéutico Biólogo"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="responsible_license">Cédula Profesional</Label>
              <Input
                id="responsible_license"
                value={config.responsible_license || ""}
                onChange={(e) => handleChange("responsible_license", e.target.value)}
                placeholder="1234567"
              />
            </div>
          </CardContent>
        </Card>

        {/* Pie de Página */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Pie de Página
            </CardTitle>
            <CardDescription>
              Texto adicional que aparecerá al final de cada reporte
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="footer_text">Texto de Pie de Página</Label>
              <Textarea
                id="footer_text"
                value={config.footer_text || ""}
                onChange={(e) => handleChange("footer_text", e.target.value)}
                placeholder="Este documento es confidencial. Los resultados son válidos únicamente para la muestra analizada."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Preview Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Vista Previa del Encabezado
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg p-6 bg-white">
            <div className="flex items-start gap-4">
              {config.logo_header && (
                <img 
                  src={config.logo_header} 
                  alt="Logo" 
                  className="w-24 h-16 object-contain"
                />
              )}
              <div className="flex-1 text-center">
                <h2 
                  className="text-xl font-bold"
                  style={{ color: config.header_color }}
                >
                  {config.lab_name || "LABORATORIO CLÍNICO"}
                </h2>
                {config.lab_slogan && (
                  <p className="text-sm text-gray-500">{config.lab_slogan}</p>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  {[config.lab_address, config.lab_phone, config.lab_email].filter(Boolean).join(" | ")}
                </p>
              </div>
            </div>
            
            {config.responsible_name && (
              <div className="mt-8 pt-4 border-t text-center">
                <div className="border-t border-gray-400 w-48 mx-auto mb-2"></div>
                <p className="font-semibold">{config.responsible_name}</p>
                {config.responsible_title && <p className="text-sm text-gray-600">{config.responsible_title}</p>}
                {config.responsible_license && <p className="text-sm text-gray-500">Cédula: {config.responsible_license}</p>}
              </div>
            )}

            {config.footer_text && (
              <p className="mt-6 text-xs text-gray-400 text-center italic">
                {config.footer_text}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Alert>
        <AlertDescription>
          <strong>Nota:</strong> Los cambios se aplicarán a todos los nuevos reportes de resultados que se generen. 
          Los reportes ya generados no se verán afectados.
        </AlertDescription>
      </Alert>
    </div>
  );
}
